import { useId } from "react";

import { CountryMapper } from "@cuauth/country-mapper";

import { useI18n } from "@/i18n";
import { joinClassNames } from "@/lib/utils";

/**
 * Renders the language selector, currently available only for Switzerland
 * locales (de-CH / fr-CH / it-CH).
 *
 * The selector follows the Figma `language-picker` layout: a label on the
 * left and an accessible segmented control on the right.
 */
export function LanguageSwitcher() {
  // Locale state and availability are provided by the shared i18n context.
  const {
    locale,
    messages,
    changeLocale,
    showLanguageSwitcher,
    availableLocales,
  } = useI18n();
  const labelId = useId();
  const radioGroupName = `${labelId}-language`;

  // The switcher is currently available only for Switzerland locales.
  if (!showLanguageSwitcher) {
    return null;
  }

  const switcherLabel = messages.shared.languageSwitcher.label;
  const selectedLanguage = CountryMapper.parseLanguageCode(locale);

  // Min width matches Figma; grow with longer CH labels. Intrinsic max width
  // feeds `--breakpoint-header-inline` in `index.css` — update both together.
  return (
    <div className="flex h-[40px] w-fit min-w-[312px] max-w-full items-center justify-between gap-1">
      {/* Visible label for the language selector. */}
      <span
        id={labelId}
        className="shrink-0 whitespace-nowrap text-[18px] font-normal leading-[1.2] text-text-secondary"
      >
        {switcherLabel}
      </span>

      {/* The radio group communicates that exactly one language can be active. */}
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        className="flex h-[40px] shrink-0 items-start gap-[4px] rounded-[8px] bg-action-tertiary p-[4px]"
      >
        {/* Keep the displayed code short while using the full locale for updates. */}
        {availableLocales.map((optionLocale) => {
          const languageCode =
            CountryMapper.parseLanguageCode(optionLocale)?.toUpperCase() ??
            optionLocale;
          const isSelected =
            CountryMapper.parseLanguageCode(optionLocale) === selectedLanguage;
          const title =
            CountryMapper.getLocaleTitle(optionLocale) ?? optionLocale;

          return (
            <label key={optionLocale} className="flex cursor-pointer">
              {/* Native radios provide focus and arrow-key behavior without custom handlers. */}
              <input
                type="radio"
                name={radioGroupName}
                value={optionLocale}
                checked={isSelected}
                aria-label={title}
                className="peer sr-only"
                onChange={() => changeLocale(optionLocale)}
              />
              <span
                aria-hidden="true"
                className={joinClassNames(
                  "flex min-h-[32px] items-center justify-center rounded-[4px] px-[16px] py-[4px]",
                  "text-[14px] font-medium leading-[1.2] transition-colors",
                  "peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-line-stroke-strong",
                  isSelected
                    ? "bg-action-primary text-action-contrast"
                    : "bg-transparent text-action-primary hover:bg-black/[0.04]",
                )}
              >
                {languageCode}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
